/**
 * Selettore destinatari (RF-02 / §11.3).
 * Esattamente UNO (XOR) tra:
 *   - filter_id           → un Filter salvato esistente
 *   - recipient_category  → una categoria rapida dell'anagrafica
 *
 * Include l'anteprima del conteggio destinatari effettivi (POST /newsletters/recipients-preview),
 * con il dettaglio delle esclusioni (blocco globale / blocco entità / senza email).
 *
 * Riferimento: TDD Sprint 18 §8.2.
 */
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Card,
  CardHeader,
  Grid,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Stack,
  Typography,
  Divider,
  CircularProgress,
} from '@mui/material';
import GroupsIcon from '@mui/icons-material/Groups';
import { CustomAutocomplete } from '../../../../utils/StyledInputs';
import { DefaultOutlinedButton } from '../../../../utils/Buttons';
import { HttpErrorHandler } from '../../../../utils/ErrorHandler';
import { usePopup } from '../../../../context/PopupContext';
import { newslettersApi, RECIPIENT_CATEGORIES, RECIPIENT_CATEGORY_LABELS } from '../../../../services/newsletter/newslettersApi';
import API from '../../../../api';

const CATEGORY_OPTIONS = RECIPIENT_CATEGORIES.map((value) => ({
  value,
  label: RECIPIENT_CATEGORY_LABELS[value] ?? value,
}));

/**
 * Normalizza la risposta dei filtri (paginata o array) in un array.
 */
function extractFilters(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.filters)) return data.filters;
  return [];
}

/**
 * @param {{
 *   entityId: string|null,
 *   mode: 'filter'|'category',
 *   onModeChange: (mode: 'filter'|'category') => void,
 *   filterId: string|null,
 *   onFilterChange: (filterId: string|null) => void,
 *   category: string|null,
 *   onCategoryChange: (category: string|null) => void,
 *   disabled?: boolean,
 *   error?: boolean,
 * }} props
 */
export function RecipientSelector({
  entityId,
  mode,
  onModeChange,
  filterId,
  onFilterChange,
  category,
  onCategoryChange,
  disabled = false,
  allowPreview = false,
  error = false,
}) {
  const { showPopup, setPopUpText } = usePopup();

  const [filters, setFilters] = useState([]);
  const [loadingFilters, setLoadingFilters] = useState(false);
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  // Carica i filtri salvati (scoping per entità mittente, quando disponibile).
  useEffect(() => {
    let active = true;
    setLoadingFilters(true);
    API.get('/filters/paginated')
      .then((res) => {
        if (!active) return;
        const all = extractFilters(res.data);
        const scoped = entityId ? all.filter((f) => !f.entity || f.entity.id === entityId) : all;
        setFilters(scoped);
      })
      .catch((err) => {
        if (!active) return;
        HttpErrorHandler(err, setPopUpText);
        showPopup(false);
      })
      .finally(() => active && setLoadingFilters(false));
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId]);

  // Reset anteprima al cambio di selezione/entità.
  useEffect(() => {
    setPreview(null);
  }, [entityId, mode, filterId, category]);

  const hasSelection = mode === 'filter' ? !!filterId : !!category;
  // Il calcolo destinatari resta possibile anche in sola lettura se allowPreview è attivo
  // (es. bozze/programmate viste nel dettaglio senza entrare in modifica).
  const canPreview = !!entityId && hasSelection && (!disabled || allowPreview);

  const handleMode = (_e, value) => {
    if (!value) return;
    onModeChange(value);
    // Garantisce l'XOR: azzera la selezione dell'altra modalità.
    if (value === 'filter') onCategoryChange(null);
    else onFilterChange(null);
  };

  const handleCalcPreview = () => {
    if (!canPreview) return;
    const payload = { entity_id: entityId };
    if (mode === 'filter') payload.filter_id = filterId;
    else payload.recipient_category = category;

    setLoadingPreview(true);
    newslettersApi
      .recipientsPreview(payload)
      .then((res) => setPreview(res.data))
      .catch((err) => {
        HttpErrorHandler(err, setPopUpText);
        showPopup(false);
      })
      .finally(() => setLoadingPreview(false));
  };

  const selectedFilter = filters.find((f) => f.id === filterId) ?? null;
  const selectedCategory = CATEGORY_OPTIONS.find((c) => c.value === category) ?? null;

  return (
    <Card sx={{ padding: '24px' }}>
      <CardHeader sx={{ padding: 0, mb: 2 }} title="Destinatari" />

      <ToggleButtonGroup
        exclusive
        size="small"
        value={mode}
        onChange={handleMode}
        disabled={disabled}
        sx={{ mb: 3 }}
      >
        <ToggleButton value="filter">Filtro salvato</ToggleButton>
        <ToggleButton value="category">Categoria rapida</ToggleButton>
      </ToggleButtonGroup>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          {mode === 'filter' ? (
            <CustomAutocomplete
              id="recipient-filter"
              disabled={disabled || loadingFilters}
              options={filters}
              value={selectedFilter}
              loading={loadingFilters}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              getOptionLabel={(option) => option.name ?? ''}
              renderOption={(props, option) => (
                <Box component="li" {...props} key={option.id}>
                  {option.name}
                </Box>
              )}
              onChange={(_e, value) => onFilterChange(value ? value.id : null)}
              renderInput={(params) => (
                <TextField {...params} label="Filtro destinatari *" variant="outlined" error={error} />
              )}
            />
          ) : (
            <CustomAutocomplete
              id="recipient-category"
              disabled={disabled}
              options={CATEGORY_OPTIONS}
              value={selectedCategory}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              getOptionLabel={(option) => option.label ?? ''}
              renderOption={(props, option) => (
                <Box component="li" {...props} key={option.value}>
                  {option.label}
                </Box>
              )}
              onChange={(_e, value) => onCategoryChange(value ? value.value : null)}
              renderInput={(params) => (
                <TextField {...params} label="Categoria destinatari *" variant="outlined" error={error} />
              )}
            />
          )}
        </Grid>

        <Grid item xs={12}>
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
            <DefaultOutlinedButton
              startIcon={loadingPreview ? <CircularProgress size={16} /> : <GroupsIcon />}
              disabled={!canPreview || loadingPreview}
              onClick={handleCalcPreview}
            >
              Calcola destinatari
            </DefaultOutlinedButton>
            {!entityId && (
              <Typography variant="body2" color="text.secondary">
                Seleziona prima l&apos;entità mittente.
              </Typography>
            )}
          </Stack>

          {preview && (
            <Box sx={{ mt: 2, p: 2, backgroundColor: '#f4f6f8', borderRadius: 1 }}>
              <Typography sx={{ fontWeight: 700, mb: 1 }}>
                Destinatari effettivi: {preview.included ?? 0}
              </Typography>
              <Divider sx={{ mb: 1 }} />
              <Typography variant="body2" color="text.secondary">
                Esclusi per blocco globale: {preview.excluded_global_optout ?? 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Esclusi per blocco entità: {preview.excluded_entity_blocked ?? 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Esclusi perché senza email: {preview.excluded_no_email ?? 0}
              </Typography>
              {/* Indirizzi rimbalzati o che hanno segnalato spam: senza questa riga il calo
                  dei destinatari non sarebbe spiegabile da chi compone la newsletter. */}
              <Typography variant="body2" color="text.secondary">
                Esclusi perché non recapitabili: {preview.excluded_suppressed ?? 0}
              </Typography>
            </Box>
          )}
        </Grid>
      </Grid>
    </Card>
  );
}

RecipientSelector.propTypes = {
  entityId: PropTypes.string,
  mode: PropTypes.oneOf(['filter', 'category']).isRequired,
  onModeChange: PropTypes.func.isRequired,
  filterId: PropTypes.string,
  onFilterChange: PropTypes.func.isRequired,
  category: PropTypes.string,
  onCategoryChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  allowPreview: PropTypes.bool,
  error: PropTypes.bool,
};

export default RecipientSelector;